import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  CalendarCheck,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Gauge,
  Globe2,
  Handshake,
  Mail,
  MapPin,
  Menu,
  Phone,
  Route as RouteIcon,
  ShieldCheck,
  Truck,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "@/components/ui/sonner";

const contact = {
  company: "Vartrum, Inc.",
  location: "Richmond, TX · Greater Houston Area",
  address: "Richmond, TX 77469",
  phone: "650.684.8741",
  phoneHref: "+16506848741",
  email: "admin@vartrum.com",
};

const navItems = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Fleet", href: "#fleet" },
  { label: "Partners", href: "#partners" },
  { label: "Quote", href: "#quote" },
];

const services = [
  {
    title: "OTR / Long Haul",
    description:
      "Nationwide freight coverage with disciplined lane execution and responsive updates from pickup through delivery.",
    icon: RouteIcon,
  },
  {
    title: "Step Deck",
    description:
      "High-clearance and specialized freight support for equipment, machinery, and complex industrial moves.",
    icon: Gauge,
  },
  {
    title: "Flatbed",
    description:
      "Open-deck hauling for construction materials, steel, lumber, palletized freight, and jobsite deliveries.",
    icon: Truck,
  },
  {
    title: "RGN Heavy Haul",
    description:
      "Heavy equipment transport coordination for oversize, overweight, and permit-sensitive freight requirements.",
    icon: ShieldCheck,
  },
];

const trustSignals = [
  {
    title: "Safety-First Operations",
    description:
      "Every load is treated with a compliance-minded approach, securement discipline, and professional driver accountability.",
    icon: ShieldCheck,
  },
  {
    title: "On-Time Execution",
    description:
      "Vartrum focuses on precise pickup windows, route planning, and clear communication with brokers and shippers.",
    icon: CalendarCheck,
  },
  {
    title: "Leased Dispatcher Network",
    description:
      "Operations are supported through multiple dispatcher relationships and established load-board activity.",
    icon: Handshake,
  },
  {
    title: "Owner-Operated Core",
    description:
      "The six active trucks are managed with ownership-level attention, supported by a broader partner network to meet demand.",
    icon: BadgeCheck,
  },
];

const stats = [
  { value: "6", label: "Active Trucks" },
  { value: "20", label: "Truck Network" },
  { value: "48", label: "State Coverage" },
  { value: "2018", label: "Business Created" },
];

const capabilities = [
  "OTR long-haul freight lanes",
  "Flatbed and open-deck cargo",
  "Step deck and high-clearance freight",
  "RGN heavy haul coordination",
  "Broker-ready documentation workflow",
  "20-truck partner network capacity",
  "Growth-stage fleet expansion plan",
];

const initialForm = {
  companyName: "",
  contactName: "",
  phone: "",
  email: "",
  origin: "",
  destination: "",
  freightType: "",
  loadWeight: "",
  preferredDates: "",
  notes: "",
};

function scrollToSection(href) {
  const target = document.querySelector(href);
  if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
}

function Header() {
  const [open, setOpen] = useState(false);

  const handleNavClick = (href) => {
    scrollToSection(href);
    setOpen(false);
  };

  return (
    <header className="site-header" data-testid="site-header">
      <a
        className="brand-mark"
        href="#top"
        data-testid="header-brand-link"
        onClick={(event) => {
          event.preventDefault();
          handleNavClick("#top");
        }}
      >
        <span className="brand-icon" data-testid="header-brand-icon">V</span>
        <span>
          <strong data-testid="header-company-name">Vartrum, Inc.</strong>
          <small data-testid="header-company-location">Richmond, TX</small>
        </span>
      </a>

      <nav className="desktop-nav" data-testid="desktop-navigation">
        {navItems.map((item) => (
          <button
            key={item.href}
            className="nav-link"
            data-testid={`desktop-nav-${item.label.toLowerCase()}-button`}
            onClick={() => handleNavClick(item.href)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <div className="header-actions" data-testid="header-contact-actions">
        <a className="phone-link" href={`tel:${contact.phoneHref}`} data-testid="header-phone-link">
          <Phone size={16} /> {contact.phone}
        </a>
        <Button
          className="gold-button compact"
          data-testid="header-request-quote-button"
          onClick={() => handleNavClick("#quote")}
        >
          Request Quote
        </Button>
        <button
          className="mobile-menu-button"
          data-testid="mobile-menu-toggle-button"
          onClick={() => setOpen((current) => !current)}
          aria-label="Toggle navigation"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="mobile-nav" data-testid="mobile-navigation-menu">
          {navItems.map((item) => (
            <button
              key={item.href}
              className="mobile-nav-link"
              data-testid={`mobile-nav-${item.label.toLowerCase()}-button`}
              onClick={() => handleNavClick(item.href)}
            >
              {item.label}
              <ChevronRight size={16} />
            </button>
          ))}
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="hero-section" data-testid="hero-section">
      <div className="hero-grid page-shell">
        <div className="hero-copy">
          <Badge className="eyebrow" data-testid="hero-eyebrow-badge">
            Texas-based commercial trucking · Growth-stage fleet
          </Badge>
          <h1 data-testid="hero-headline">Reliable Freight Solutions Across America</h1>
          <p data-testid="hero-value-proposition">
            Vartrum, Inc. is an over-the-road trucking and logistics company built for brokers, shippers,
            and lenders who expect disciplined operations, credible documentation, and dependable execution.
          </p>
          <div className="hero-actions" data-testid="hero-action-group">
            <Button
              className="gold-button"
              data-testid="hero-request-quote-button"
              onClick={() => scrollToSection("#quote")}
            >
              Request a Quote <ArrowRight size={18} />
            </Button>
            <a className="outline-link" href={`mailto:${contact.email}`} data-testid="hero-email-link">
              <Mail size={18} /> {contact.email}
            </a>
          </div>
        </div>

        <div className="hero-visual" data-testid="hero-truck-image-card">
          <img
            src="https://images.pexels.com/photos/27099095/pexels-photo-27099095.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Commercial semi truck operating on an open highway"
            data-testid="hero-truck-image"
          />
          <div className="hero-image-overlay" />
          <div className="hero-credential-card" data-testid="hero-credential-card">
            <span data-testid="hero-credential-label">Operational profile</span>
            <strong data-testid="hero-credential-value">6 active trucks</strong>
            <small data-testid="hero-credential-note">20-truck partner network · Flatbed · Step Deck · RGN</small>
          </div>
        </div>
      </div>
      <div className="stats-bar page-shell" data-testid="stats-bar">
        {stats.map((stat) => (
          <div className="stat-item" key={stat.label} data-testid={`stat-${stat.label.toLowerCase().replaceAll(" ", "-")}`}>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="section about-section" data-testid="about-section">
      <div className="page-shell split-section">
        <div>
          <p className="section-kicker" data-testid="about-kicker">About Vartrum</p>
          <h2 data-testid="about-heading">Texas roots. National freight discipline.</h2>
        </div>
        <div className="rich-copy" data-testid="about-copy">
          <p>
            Headquartered in Richmond, Texas, Vartrum, Inc. operates as a business-forward trucking company
            serving long-haul and specialized freight needs across the continental United States. The business was
            created in 2018, with its trucking division built around a clear focus: reliable capacity, professional
            communication, and mature operating practices.
          </p>
          <p>
            Vartrum runs six active trucks and partners with a broader network of carriers to meet market demand.
            This network provides access to roughly 20 trucks while preserving Vartrum’s focus on controlled growth,
            dependable load execution, and lender-ready operational transparency.
          </p>
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="section services-section" data-testid="services-section">
      <div className="page-shell">
        <div className="section-heading-row">
          <div>
            <p className="section-kicker" data-testid="services-kicker">Core Services</p>
            <h2 data-testid="services-heading">Built for complex freight, long lanes, and accountable execution.</h2>
          </div>
          <p className="section-summary" data-testid="services-summary">
            From open-deck freight to heavy haul coordination, Vartrum supports loads that require planning,
            securement focus, and clear communication.
          </p>
        </div>
        <div className="service-grid" data-testid="services-grid">
          {services.map((service) => {
            const Icon = service.icon;
            return (
              <Card className="service-card" key={service.title} data-testid={`service-card-${service.title.toLowerCase().replaceAll(" / ", "-").replaceAll(" ", "-")}`}>
                <CardContent className="service-card-content">
                  <div className="service-icon" data-testid={`service-icon-${service.title.toLowerCase().replaceAll(" / ", "-").replaceAll(" ", "-")}`}>
                    <Icon size={24} />
                  </div>
                  <h3 data-testid={`service-title-${service.title.toLowerCase().replaceAll(" / ", "-").replaceAll(" ", "-")}`}>{service.title}</h3>
                  <p data-testid={`service-description-${service.title.toLowerCase().replaceAll(" / ", "-").replaceAll(" ", "-")}`}>{service.description}</p>
                  <Button
                    variant="ghost"
                    className="service-cta"
                    data-testid={`service-quote-button-${service.title.toLowerCase().replaceAll(" / ", "-").replaceAll(" ", "-")}`}
                    onClick={() => scrollToSection("#quote")}
                  >
                    Request a Quote <ArrowRight size={16} />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Fleet() {
  return (
    <section id="fleet" className="section fleet-section" data-testid="fleet-section">
      <div className="page-shell fleet-grid">
        <div className="fleet-panel" data-testid="fleet-capabilities-panel">
          <p className="section-kicker" data-testid="fleet-kicker">Fleet / Capabilities</p>
          <h2 data-testid="fleet-heading">A six-truck fleet with national reach and expansion momentum.</h2>
          <p data-testid="fleet-summary">
            Vartrum supports 48-state freight coverage through OTR operations and specialized open-deck capabilities.
            The company operates six active trucks and can coordinate additional capacity through a trusted 20-truck
            partner network aligned to high-demand industrial freight.
          </p>
          <div className="capability-list" data-testid="capability-list">
            {capabilities.map((item) => (
              <div className="capability-item" key={item} data-testid={`capability-${item.toLowerCase().replaceAll(" ", "-")}`}>
                <CheckCircle2 size={18} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="compliance-panel" data-testid="operations-readiness-panel">
          <div className="panel-header">
            <ClipboardCheck size={28} />
            <span data-testid="operations-readiness-label">Business readiness</span>
          </div>
          <h3 data-testid="operations-readiness-heading">Documentation-led operating model</h3>
          <p data-testid="operations-readiness-copy">
            Vartrum maintains a broker-ready workflow for insurance documents, dispatcher coordination, load details,
            equipment availability, and route requirements. DOT/MC authority is not represented directly; Vartrum
            operates through leased dispatcher relationships and partner capacity as disclosed.
          </p>
          <div className="readiness-badges" data-testid="readiness-badges">
            <Badge data-testid="readiness-badge-insurance">Insurance Docs Available</Badge>
            <Badge data-testid="readiness-badge-dispatchers">Leased Dispatch Network</Badge>
            <Badge data-testid="readiness-badge-growth">Fleet Expansion Plan</Badge>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyChooseUs() {
  return (
    <section className="section trust-section" data-testid="why-choose-us-section">
      <div className="page-shell">
        <div className="section-heading-row narrow">
          <div>
            <p className="section-kicker" data-testid="trust-kicker">Why Choose Us</p>
            <h2 data-testid="trust-heading">Credibility signals that matter to brokers, shippers, and lenders.</h2>
          </div>
        </div>
        <div className="trust-grid" data-testid="trust-grid">
          {trustSignals.map((item) => {
            const Icon = item.icon;
            return (
              <div className="trust-card" key={item.title} data-testid={`trust-card-${item.title.toLowerCase().replaceAll(" ", "-")}`}>
                <Icon size={24} data-testid={`trust-icon-${item.title.toLowerCase().replaceAll(" ", "-")}`} />
                <h3 data-testid={`trust-title-${item.title.toLowerCase().replaceAll(" ", "-")}`}>{item.title}</h3>
                <p data-testid={`trust-description-${item.title.toLowerCase().replaceAll(" ", "-")}`}>{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Partners() {
  return (
    <section id="partners" className="section partners-section" data-testid="partners-section">
      <div className="page-shell partners-card">
        <div>
          <p className="section-kicker" data-testid="partners-kicker">Broker & Shipper Partners</p>
          <h2 data-testid="partners-heading">Consistent freight sourcing through active market relationships.</h2>
        </div>
        <div className="partners-copy" data-testid="partners-copy">
          <p>
            Vartrum works through established dispatcher relationships, partner capacity, and load-board channels to
            maintain access to freight opportunities across long-haul and specialized lanes. The company is positioned
            to serve brokers and shippers that value clear communication, professional documentation, and reliable follow-through.
          </p>
          <div className="partner-pill-row" data-testid="partner-pill-row">
            <span data-testid="partner-pill-broker-ready">Broker-ready packets</span>
            <span data-testid="partner-pill-load-board">Load-board presence</span>
            <span data-testid="partner-pill-dispatcher-network">Multiple dispatcher relationships</span>
            <span data-testid="partner-pill-truck-network">20-truck partner network</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function QuoteForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const requiredFields = [
    "companyName",
    "contactName",
    "phone",
    "email",
    "origin",
    "destination",
    "freightType",
    "loadWeight",
    "preferredDates",
  ];

  const validate = () => {
    const nextErrors = {};
    requiredFields.forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "Required";
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required quote fields.");
      return;
    }
    setSubmitted(true);
    toast.success("Quote request received. Vartrum will follow up with next steps.");
    setForm(initialForm);
    setErrors({});
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const fieldProps = (field, label) => ({
    id: field,
    value: form[field],
    onChange: (event) => updateField(field, event.target.value),
    "aria-label": label,
    "aria-invalid": Boolean(errors[field]),
    "data-testid": `quote-form-${field.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}-input`,
  });

  return (
    <section id="quote" className="section quote-section" data-testid="quote-section">
      <div className="page-shell quote-grid">
        <div className="quote-intro" data-testid="quote-intro-panel">
          <p className="section-kicker" data-testid="quote-kicker">Contact / Get a Quote</p>
          <h2 data-testid="quote-heading">Move freight with a company built for accountable growth.</h2>
          <p data-testid="quote-summary">
            Share the load details and Vartrum will confirm equipment fit, scheduling availability, and next-step
            documentation. Form delivery can be connected later; for now, submissions show an on-page confirmation.
          </p>
          <div className="contact-stack" data-testid="quote-contact-stack">
            <a href={`tel:${contact.phoneHref}`} data-testid="quote-phone-link"><Phone size={18} /> {contact.phone}</a>
            <a href={`mailto:${contact.email}`} data-testid="quote-email-link"><Mail size={18} /> {contact.email}</a>
            <span data-testid="quote-location-text"><MapPin size={18} /> {contact.location}</span>
          </div>
        </div>

        <form className="quote-form" onSubmit={handleSubmit} noValidate data-testid="quote-form">
          <div className="form-grid">
            <label data-testid="quote-form-company-name-label">
              Company name *
              <Input {...fieldProps("companyName", "Company name")} placeholder="Company or broker name" />
              {errors.companyName && <small data-testid="quote-form-company-name-error">{errors.companyName}</small>}
            </label>
            <label data-testid="quote-form-contact-name-label">
              Contact name *
              <Input {...fieldProps("contactName", "Contact name")} placeholder="Primary contact" />
              {errors.contactName && <small data-testid="quote-form-contact-name-error">{errors.contactName}</small>}
            </label>
            <label data-testid="quote-form-phone-label">
              Phone *
              <Input {...fieldProps("phone", "Phone")} placeholder="Best phone number" />
              {errors.phone && <small data-testid="quote-form-phone-error">{errors.phone}</small>}
            </label>
            <label data-testid="quote-form-email-label">
              Email *
              <Input {...fieldProps("email", "Email")} type="email" placeholder="name@company.com" />
              {errors.email && <small data-testid="quote-form-email-error">{errors.email}</small>}
            </label>
            <label data-testid="quote-form-origin-label">
              Origin *
              <Input {...fieldProps("origin", "Origin")} placeholder="City, state" />
              {errors.origin && <small data-testid="quote-form-origin-error">{errors.origin}</small>}
            </label>
            <label data-testid="quote-form-destination-label">
              Destination *
              <Input {...fieldProps("destination", "Destination")} placeholder="City, state" />
              {errors.destination && <small data-testid="quote-form-destination-error">{errors.destination}</small>}
            </label>
            <label data-testid="quote-form-freight-type-label">
              Freight type *
              <Input {...fieldProps("freightType", "Freight type")} placeholder="Flatbed, step deck, RGN, OTR" />
              {errors.freightType && <small data-testid="quote-form-freight-type-error">{errors.freightType}</small>}
            </label>
            <label data-testid="quote-form-load-weight-label">
              Load weight *
              <Input {...fieldProps("loadWeight", "Load weight")} placeholder="Approximate weight" />
              {errors.loadWeight && <small data-testid="quote-form-load-weight-error">{errors.loadWeight}</small>}
            </label>
            <label className="full-span" data-testid="quote-form-preferred-dates-label">
              Preferred dates *
              <Input {...fieldProps("preferredDates", "Preferred dates")} placeholder="Pickup and delivery timing" />
              {errors.preferredDates && <small data-testid="quote-form-preferred-dates-error">{errors.preferredDates}</small>}
            </label>
            <label className="full-span" data-testid="quote-form-notes-label">
              Additional details
              <Textarea
                {...fieldProps("notes", "Additional details")}
                placeholder="Dimensions, permits, tarping, appointment details, or special handling requirements"
              />
            </label>
          </div>
          <Button className="gold-button submit-button" type="submit" data-testid="quote-form-submit-button">
            Submit Quote Request <ArrowRight size={18} />
          </Button>
          {submitted && (
            <div className="success-message" data-testid="quote-form-success-message">
              <CheckCircle2 size={20} /> Quote request received. A Vartrum representative will review the details and follow up.
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="page-shell footer-grid">
        <div data-testid="footer-company-block">
          <a className="brand-mark footer-brand" href="#top" data-testid="footer-brand-link" onClick={(event) => { event.preventDefault(); scrollToSection("#top"); }}>
            <span className="brand-icon" data-testid="footer-brand-icon">V</span>
            <span>
              <strong data-testid="footer-company-name">{contact.company}</strong>
              <small data-testid="footer-company-location">Commercial Trucking & Logistics</small>
            </span>
          </a>
          <p data-testid="footer-description">
            Over-the-road freight, open-deck capability, and specialized heavy haul coordination from Richmond, Texas.
          </p>
        </div>
        <div className="footer-info" data-testid="footer-contact-info">
          <span data-testid="footer-address"><MapPin size={16} /> {contact.address}</span>
          <a href={`tel:${contact.phoneHref}`} data-testid="footer-phone-link"><Phone size={16} /> {contact.phone}</a>
          <a href={`mailto:${contact.email}`} data-testid="footer-email-link"><Mail size={16} /> {contact.email}</a>
        </div>
        <div className="footer-compliance" data-testid="footer-compliance-note">
          <strong data-testid="footer-compliance-title">Compliance note</strong>
          <span data-testid="footer-compliance-text">No direct DOT/MC number represented. Operations supported through leased dispatcher relationships and partner carrier capacity.</span>
        </div>
      </div>
      <div className="page-shell footer-bottom" data-testid="footer-copyright">
        © {new Date().getFullYear()} Vartrum, Inc. All rights reserved.
      </div>
    </footer>
  );
}

const Home = () => (
  <main className="app-shell" data-testid="home-page">
    <Header />
    <Hero />
    <About />
    <Services />
    <Fleet />
    <WhyChooseUs />
    <Partners />
    <QuoteForm />
    <Footer />
    <Toaster richColors position="top-right" />
  </main>
);

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
